import type { TransferListItem } from "@/api/custom_types";
import { toFacetFilterOptions } from "@/components";
import { useApiError } from "@/hooks";
import { useTranslation } from "@/hooks/useTranslation";
import { useGetSenderTransferList } from "@/services";
import { getTimeSinceText } from "@/utils/timestamp";
import { formatNumber } from "@/utils/tools";
import {
	Card,
	CardContent,
	Chip,
	type ChipVariant,
	DataTable,
	type FilterConfig,
	GenericFilters,
	Skeleton,
	Typography,
	useServerTableState,
} from "@st-marketplace/ui";
import type { ColumnDef } from "@tanstack/react-table";
import { type FC, useMemo, useState } from "react";

type ItemState = "listed" | "sold" | "expired" | "not_listed";

const STATE_OF_TRADE_STATE: Record<string, ItemState> = {
	active: "listed",
	closed: "sold",
	expired: "expired",
};

const STATE_DISPLAY: Record<
	ItemState,
	{
		variant: ChipVariant;
		key: "stateListed" | "stateSold" | "stateExpired" | "stateNotListed";
	}
> = {
	listed: { variant: "info", key: "stateListed" },
	sold: { variant: "success", key: "stateSold" },
	expired: { variant: "warning", key: "stateExpired" },
	not_listed: { variant: "muted", key: "stateNotListed" },
};

const stateOf = (item: TransferListItem): ItemState =>
	(item.trade_state && STATE_OF_TRADE_STATE[item.trade_state]) || "not_listed";

type Filters = { state: string[]; type: string[] };
const EMPTY_FILTERS: Filters = { state: [], type: [] };

const matches = (values: string[], value: string) =>
	values.length === 0 || values.includes(value);

/** Facet counts: `total` over all items, `filtered` over items passing the other filters. */
function countFacet(
	items: TransferListItem[],
	keyOf: (item: TransferListItem) => string,
	passesOthers: (item: TransferListItem) => boolean,
) {
	const counts = new Map<string, { total: number; filtered: number }>();
	for (const item of items) {
		const c = counts.get(keyOf(item)) ?? { total: 0, filtered: 0 };
		c.total += 1;
		if (passesOthers(item)) c.filtered += 1;
		counts.set(keyOf(item), c);
	}
	return [...counts].map(([value, c]) => ({ value, ...c }));
}

/** "2h 15m" until `expiresAt`, or null when it's not in the future. */
const formatRemaining = (expiresAt: string): string | null => {
	const minutes = Math.floor(
		(new Date(expiresAt).getTime() - Date.now()) / 60_000,
	);
	if (minutes <= 0) return null;
	const days = Math.floor(minutes / 1440);
	const hours = Math.floor((minutes % 1440) / 60);
	const mins = minutes % 60;
	if (days > 0) return `${days}d ${hours}h`;
	return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

interface SenderInfoTransferListProps {
	senderId: string;
}

const SenderInfoTransferList: FC<SenderInfoTransferListProps> = ({
	senderId,
}) => {
	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();
	const server = useServerTableState({ initialPageSize: 20 });
	const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);

	const { data, isLoading, isFetching, error, refetch } =
		useGetSenderTransferList({ path: { sender_id: senderId } });

	const items = data?.items ?? [];
	const players = items.filter((item) => item.item_type === "player");

	// highest value first, unpriced items last
	const visibleRows = useMemo(
		() =>
			(data?.items ?? [])
				.filter(
					(item) =>
						matches(filters.state, stateOf(item)) &&
						matches(filters.type, item.item_type),
				)
				.sort((a, b) => (b.price ?? -1) - (a.price ?? -1)),
		[data?.items, filters],
	);
	const pageRows = visibleRows.slice(
		server.pageIndex * server.pageSize,
		(server.pageIndex + 1) * server.pageSize,
	);

	const filterConfig = useMemo<FilterConfig<Filters>>(() => {
		const all = data?.items ?? [];
		const stateFacets = countFacet(all, stateOf, (item) =>
			matches(filters.type, item.item_type),
		);
		const typeFacets = countFacet(
			all,
			(item) => item.item_type,
			(item) => matches(filters.state, stateOf(item)),
		);
		return {
			fields: [
				{
					key: "state",
					type: "multi-select",
					placeholder: t("senderInfo.transferList.tableState"),
					options: toFacetFilterOptions(stateFacets, (state) =>
						t(
							`senderInfo.transferList.${
								STATE_DISPLAY[state as ItemState].key
							}`,
						),
					),
				},
				{
					key: "type",
					type: "multi-select",
					placeholder: t("senderInfo.transferList.tableType"),
					options: toFacetFilterOptions(typeFacets),
					searchable: true,
				},
			],
			searchDebounceMs: 0,
			showClearButton: true,
		};
	}, [data?.items, filters, t]);

	const onFiltersChange = (patch: Partial<Filters>) => {
		setFilters((prev) => ({ ...prev, ...patch }));
		server.onPageChange(1);
	};

	const columns = useMemo<ColumnDef<TransferListItem>[]>(
		() => [
			{
				id: "item",
				header: t("senderInfo.transferList.tableItem"),
				enableSorting: false,
				cell: ({ row }) => {
					const { name, rating, rarity_name, resource_id, picture } =
						row.original;
					return (
						<div className="flex items-center gap-3">
							{picture ? (
								<img
									src={picture}
									alt={name ?? "card"}
									loading="lazy"
									decoding="async"
									referrerPolicy="no-referrer"
									className="h-14 w-11 shrink-0 object-contain"
								/>
							) : (
								<div className="h-14 w-11 shrink-0" />
							)}
							<Typography variant="body-sm" className="font-semibold">
								{name
									? [name, rating, rarity_name].filter(Boolean).join(" ")
									: resource_id ?? "-"}
							</Typography>
						</div>
					);
				},
			},
			{
				id: "type",
				header: t("senderInfo.transferList.tableType"),
				enableSorting: false,
				cell: ({ row }) => row.original.item_type,
			},
			{
				id: "state",
				header: t("senderInfo.transferList.tableState"),
				enableSorting: false,
				cell: ({ row }) => {
					const { variant, key } = STATE_DISPLAY[stateOf(row.original)];
					return (
						<Chip variant={variant} size="sm">
							{t(`senderInfo.transferList.${key}`)}
						</Chip>
					);
				},
			},
			{
				id: "expires",
				header: t("senderInfo.transferList.tableExpires"),
				enableSorting: false,
				cell: ({ row }) => {
					const { expires_at, trade_state } = row.original;
					if (trade_state !== "active" || !expires_at) return "-";
					return (
						formatRemaining(expires_at) ??
						t("senderInfo.transferList.stateExpired")
					);
				},
			},
			{
				id: "buy_now",
				header: t("senderInfo.transferList.tableBuyNow"),
				enableSorting: false,
				cell: ({ row }) =>
					row.original.buy_now_price
						? formatNumber(row.original.buy_now_price)
						: "-",
			},
			{
				id: "value",
				header: t("senderInfo.transferList.tableValue"),
				enableSorting: false,
				cell: ({ row }) =>
					row.original.price != null ? formatNumber(row.original.price) : "-",
			},
		],
		[t],
	);

	return (
		<div className="flex flex-col gap-4">
			<Card className="rounded-md">
				<CardContent className="flex flex-wrap items-end justify-between gap-4 p-4">
					<div>
						<Typography variant="body-sm" className="text-secondary-text-100">
							{t("senderInfo.transferList.virtualCoins")}
						</Typography>
						{isLoading ? (
							<Skeleton className="h-8 w-40" />
						) : (
							<Typography variant="title-md" as="p">
								{formatNumber(data?.virtual_coins ?? 0)}
							</Typography>
						)}
						<Typography variant="caption" className="text-secondary-text-100">
							{t("senderInfo.transferList.virtualCoinsHint")}
						</Typography>
					</div>
					{!isLoading && (
						<div className="text-end">
							<Typography variant="body-sm">
								{t("senderInfo.transferList.itemsCount", {
									players: players.length,
									items: items.length,
								})}
							</Typography>
							<Typography variant="caption" className="text-secondary-text-100">
								{data?.updated_at
									? `${t(
											"senderInfo.transferList.updatedAt",
										)}: ${getTimeSinceText(data.updated_at, t, lang)}`
									: t("senderInfo.transferList.notCaptured")}
							</Typography>
						</div>
					)}
				</CardContent>
			</Card>

			<GenericFilters
				config={filterConfig}
				filters={filters}
				onFiltersChange={onFiltersChange}
				onClearFilters={() => onFiltersChange(EMPTY_FILTERS)}
			/>

			<DataTable
				columns={columns}
				data={pageRows}
				totalItems={visibleRows.length}
				server={server}
				isLoading={isLoading}
				isFetching={isFetching}
				error={error ? getErrorInterface(error) : null}
				onRetry={refetch}
				getRowId={(row) => row.trade_id}
			/>
		</div>
	);
};

export default SenderInfoTransferList;
